/**
 * Sales — period-aware financial analytics.
 * Revenue tab: KPIs with deltas, trend chart, payment split.
 * Orders tab: order quality stats, force-close breakdown.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { BarChart3, CreditCard, HandCoins, Percent, Receipt, TrendingUp, Users, Wallet } from 'lucide-react';
import { formatAmd, resolveAppLocale } from '../locale.js';
import { supabase } from '../supabase.js';
import {
  Callout,
  Card,
  CardHeader,
  CHART_TOOLTIP,
  DateRange,
  PageHeader,
  resolveDateRangeDates,
  Stat,
  Stats,
  Tabs,
  type DateRangeValue,
} from '../ui/index.js';

const fmtPct = (n: number) => `${n.toFixed(1)}%`;

// ─── Period helpers ───────────────────────────────────────────────────────────

type SalesTab = 'revenue' | 'orders';

function startOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

/** Range for the selected preset; open-ended presets end "now" so today's partial day is included. */
function periodRange(v: DateRangeValue): [Date, Date] {
  const [start, end] = resolveDateRangeDates(v);
  const now = new Date();
  return [start, end > now ? now : end];
}

function prevRange(start: Date, end: Date): [Date, Date] {
  const len = end.getTime() - start.getTime();
  const pe = new Date(start.getTime() - 1);
  return [new Date(pe.getTime() - len), pe];
}

function dayLbl(d: Date, locale: string) {
  return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
}
function hourLbl(d: Date) {
  return String(d.getHours()).padStart(2, '0') + 'h';
}

/** Walk each calendar day from start through end (inclusive). */
function eachDayInRange(start: Date, end: Date): Date[] {
  const days: Date[] = [];
  const cur = startOfDay(start);
  const last = startOfDay(end);
  while (cur <= last) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

/** PostgREST caps responses at 1000 rows — paginate in 1000-row chunks. */
const FETCH_PAGE = 1000;

async function fetchPaged<T>(
  query: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
): Promise<T[]> {
  const rows: T[] = [];
  for (let offset = 0; ; offset += FETCH_PAGE) {
    const { data, error } = await query(offset, offset + FETCH_PAGE - 1);
    if (error) throw new Error(error.message);
    const batch = data ?? [];
    rows.push(...batch);
    if (batch.length < FETCH_PAGE) break;
  }
  return rows;
}

// ─── Data types ───────────────────────────────────────────────────────────────

interface KpiSet {
  revenue: number;
  orders: number;
  pax: number;
}

interface TrendPt {
  label: string;
  revenue: number;
}

interface PaySplit {
  method: 'cash' | 'card';
  value: number;
  color: string;
}

interface OrdStats {
  total: number;
  paid: number;
  refunded: number;
  voided: number;
  forced: number;
}

interface ReasonPt {
  reason: string;
  value: number;
  color: string;
}

interface TipStats {
  total: number;
  prevTotal: number;
  tippedOrders: number;
  prevTippedOrders: number;
  cardOrders: number;
  prevCardOrders: number;
}

interface SalesData {
  cur: KpiSet;
  prev: KpiSet;
  tips: TipStats;
  trend: TrendPt[];
  paymentSplit: PaySplit[];
  ordStats: OrdStats;
  reasons: ReasonPt[];
  avgItems: number | null;
  modRate: number | null;
}

// ─── Fetch ────────────────────────────────────────────────────────────────────

async function fetchSales(
  clientId: string,
  start: Date,
  end: Date,
  isToday: boolean,
  locale: string,
): Promise<SalesData> {
  if (!supabase) throw new Error('Supabase not configured');
  const db = supabase;
  const si = start.toISOString();
  const ei = end.toISOString();
  const [ps, pe] = prevRange(start, end);
  const psi = ps.toISOString();
  const pei = pe.toISOString();

  const [paidRows, allRows, prevRows, payRows, prevPayRows, itemRows] = await Promise.all([
    fetchPaged((from, to) =>
      db
        .from('orders')
        .select('id, total, pax, paid_at, tip_amount')
        .eq('client_id', clientId)
        .eq('status', 'paid')
        .gte('paid_at', si)
        .lte('paid_at', ei)
        .order('paid_at', { ascending: true })
        .range(from, to),
    ),

    fetchPaged((from, to) =>
      db
        .from('orders')
        .select('status, forced_close, close_reason')
        .eq('client_id', clientId)
        .gte('created_at', si)
        .lte('created_at', ei)
        .order('created_at', { ascending: true })
        .range(from, to),
    ),

    fetchPaged((from, to) =>
      db
        .from('orders')
        .select('id, total, pax, tip_amount')
        .eq('client_id', clientId)
        .eq('status', 'paid')
        .gte('paid_at', psi)
        .lte('paid_at', pei)
        .order('paid_at', { ascending: true })
        .range(from, to),
    ),

    fetchPaged((from, to) =>
      db
        .from('payments')
        .select('method, amount, order_id')
        .eq('client_id', clientId)
        .eq('status', 'paid')
        .gte('created_at', si)
        .lte('created_at', ei)
        .order('created_at', { ascending: true })
        .range(from, to),
    ),

    fetchPaged((from, to) =>
      db
        .from('payments')
        .select('method, order_id')
        .eq('client_id', clientId)
        .eq('status', 'paid')
        .gte('created_at', psi)
        .lte('created_at', pei)
        .order('created_at', { ascending: true })
        .range(from, to),
    ),

    fetchPaged((from, to) =>
      db
        .from('order_items')
        .select('order_id, modifiers, orders!inner(status, paid_at)')
        .eq('client_id', clientId)
        .filter('orders.status', 'eq', 'paid')
        .filter('orders.paid_at', 'gte', si)
        .filter('orders.paid_at', 'lte', ei)
        .order('id', { ascending: true })
        .range(from, to),
    ),
  ]);

  // ── KPIs
  const cur: KpiSet = {
    revenue: paidRows.reduce((s, o) => s + Number(o.total), 0),
    orders: paidRows.length,
    pax: paidRows.reduce((s, o) => s + (o.pax ?? 1), 0),
  };
  const prev: KpiSet = {
    revenue: prevRows.reduce((s, o) => s + Number(o.total), 0),
    orders: prevRows.length,
    pax: prevRows.reduce((s, o) => s + (o.pax ?? 1), 0),
  };

  const tipOf = (rows: Array<{ tip_amount?: number | null }>) =>
    rows.reduce((s, o) => s + (Number(o.tip_amount) > 0 ? Number(o.tip_amount) : 0), 0);
  const tippedCount = (rows: Array<{ tip_amount?: number | null }>) =>
    rows.filter((o) => Number(o.tip_amount) > 0).length;
  const cardOrderCount = (rows: Array<{ method: string; order_id: string }>) =>
    new Set(rows.filter((p) => p.method === 'card').map((p) => p.order_id)).size;

  const paidOrderIds = new Set(paidRows.map((o) => o.id));
  const prevOrderIds = new Set(prevRows.map((o) => o.id));
  const curPayRows = payRows.filter((p) => paidOrderIds.has(p.order_id));
  const prevPayRowsFiltered = prevPayRows.filter((p) => prevOrderIds.has(p.order_id));

  const tips: TipStats = {
    total: tipOf(paidRows),
    prevTotal: tipOf(prevRows),
    tippedOrders: tippedCount(paidRows),
    prevTippedOrders: tippedCount(prevRows),
    cardOrders: cardOrderCount(curPayRows),
    prevCardOrders: cardOrderCount(prevPayRowsFiltered),
  };

  // ── Revenue trend (hourly for today, daily otherwise)
  const trendMap = new Map<string, number>();
  if (isToday) {
    for (let h = 0; h < 24; h++) trendMap.set(String(h).padStart(2, '0') + 'h', 0);
    for (const o of paidRows) {
      if (!o.paid_at) continue;
      const lbl = hourLbl(new Date(o.paid_at));
      trendMap.set(lbl, (trendMap.get(lbl) ?? 0) + Number(o.total));
    }
  } else {
    for (const d of eachDayInRange(start, end)) {
      trendMap.set(dayLbl(d, locale), 0);
    }
    for (const o of paidRows) {
      if (!o.paid_at) continue;
      const lbl = dayLbl(new Date(o.paid_at), locale);
      if (trendMap.has(lbl)) trendMap.set(lbl, (trendMap.get(lbl) ?? 0) + Number(o.total));
    }
  }
  const trend: TrendPt[] = Array.from(trendMap, ([label, revenue]) => ({ label, revenue }));

  // ── Payment split (only paid orders — exclude refunded/void with lingering payments)
  const cashAmt = curPayRows
    .filter((p) => p.method === 'cash')
    .reduce((s, p) => s + Number(p.amount), 0);
  const cardAmt = curPayRows
    .filter((p) => p.method === 'card')
    .reduce((s, p) => s + Number(p.amount), 0);
  const paymentSplit: PaySplit[] = (
    [
      { method: 'cash' as const, value: Math.round(cashAmt), color: 'var(--chart-6)' },
      { method: 'card' as const, value: Math.round(cardAmt), color: 'var(--chart-1)' },
    ] satisfies PaySplit[]
  ).filter((e) => e.value > 0);

  // ── Order status breakdown
  const ordStats: OrdStats = {
    total: allRows.length,
    paid: allRows.filter((o) => o.status === 'paid').length,
    refunded: allRows.filter((o) => o.status === 'refunded').length,
    voided: allRows.filter((o) => o.status === 'void').length,
    forced: allRows.filter((o) => o.forced_close).length,
  };

  // ── Force-close by reason
  const reasonMap = new Map<string, number>();
  for (const o of allRows) {
    if (o.forced_close && o.close_reason) {
      const r = o.close_reason as string;
      reasonMap.set(r, (reasonMap.get(r) ?? 0) + 1);
    }
  }
  const REASON_COLORS: Record<string, string> = {
    mistake: 'var(--chart-3)',
    system_error: '#ff3b30',
    free: 'var(--chart-2)',
    other: 'var(--chart-4)',
  };
  const reasons: ReasonPt[] = Array.from(reasonMap, ([reason, value]) => ({
    reason,
    value,
    color: REASON_COLORS[reason] ?? 'var(--chart-4)',
  })).sort((a, b) => b.value - a.value);

  // ── Order quality
  let avgItems: number | null = null;
  let modRate: number | null = null;
  if (itemRows.length > 0 && cur.orders > 0) {
    avgItems = itemRows.length / cur.orders;
    const withMods = new Set(
      itemRows
        .filter((i) => Array.isArray(i.modifiers) && (i.modifiers as unknown[]).length > 0)
        .map((i) => i.order_id),
    );
    modRate = (withMods.size / cur.orders) * 100;
  }

  return { cur, prev, tips, trend, paymentSplit, ordStats, reasons, avgItems, modRate };
}

const TS = CHART_TOOLTIP;

const REASON_I18N: Record<string, string> = {
  mistake: 'sales.reasonMistake',
  system_error: 'sales.reasonSystemError',
  free: 'sales.reasonFree',
  other: 'sales.reasonOther',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function pctDelta(cur: number, prev: number): number | null {
  if (prev === 0 || isNaN(cur) || isNaN(prev)) return null;
  return ((cur - prev) / prev) * 100;
}

function TrendChart({ data, isToday }: { data: TrendPt[]; isToday: boolean }) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const interval = isToday ? 2 : data.length > 15 ? 4 : 1;
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 10, fill: 'var(--text-subtle)' }}
          interval={interval}
        />
        <YAxis hide />
        <Tooltip
          formatter={(v: unknown) => [AMD(Number(v)), t('sales.revenue')]}
          contentStyle={TS}
          cursor={{ fill: 'var(--surface-hover)' }}
        />
        <Bar dataKey="revenue" fill="var(--info)" radius={[3, 3, 0, 0]} maxBarSize={28} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function PaySplitChart({ data }: { data: PaySplit[] }) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const methodLabel = (m: PaySplit['method']) =>
    m === 'cash' ? t('sales.cash') : t('sales.card');

  if (!data.length) return <p className="chart-empty">{t('sales.noPaymentData')}</p>;
  const total = data.reduce((s, d) => s + d.value, 0);
  const chartData = data.map((d) => ({ ...d, name: methodLabel(d.method) }));
  return (
    <div>
      <ResponsiveContainer width="100%" height={160}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={44}
            outerRadius={68}
            dataKey="value"
            paddingAngle={3}
          >
            {data.map((e, i) => (
              <Cell key={i} fill={e.color} />
            ))}
          </Pie>
          <Tooltip formatter={(v: unknown) => [AMD(Number(v)), '']} contentStyle={TS} />
        </PieChart>
      </ResponsiveContainer>
      <div className="pay-legend">
        {data.map((d) => (
          <div key={d.method} className="pay-legend-row">
            <span className="pay-legend-dot" style={{ background: d.color }} />
            <span className="pay-legend-name">{methodLabel(d.method)}</span>
            <span className="pay-legend-pct">
              {total > 0 ? Math.round((d.value / total) * 100) : 0}%
            </span>
            <span className="pay-legend-val">{AMD(d.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="sales-stat-box">
      <div className="sales-stat-val">{value}</div>
      <div className="sales-stat-label">{label}</div>
    </div>
  );
}

function ReasonChart({ data }: { data: ReasonPt[] }) {
  const { t } = useTranslation();
  const reasonLabel = (reason: string) =>
    REASON_I18N[reason] ? t(REASON_I18N[reason]!) : reason.replace('_', ' ');

  if (!data.length) return <p className="chart-empty">{t('sales.noForceClosed')}</p>;
  const chartData = data.map((d) => ({ ...d, name: reasonLabel(d.reason) }));
  return (
    <div>
      <ResponsiveContainer width="100%" height={160}>
        <PieChart>
          <Pie data={chartData} cx="50%" cy="50%" outerRadius={68} dataKey="value" paddingAngle={2}>
            {data.map((e, i) => (
              <Cell key={i} fill={e.color} />
            ))}
          </Pie>
          <Tooltip
            formatter={(v: unknown, n: unknown) => [String(v), String(n)]}
            contentStyle={TS}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="pay-legend">
        {data.map((d) => (
          <div key={d.reason} className="pay-legend-row">
            <span className="pay-legend-dot" style={{ background: d.color }} />
            <span className="pay-legend-name" style={{ textTransform: 'capitalize' }}>
              {reasonLabel(d.reason)}
            </span>
            <span className="pay-legend-pct">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export function SalesView({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const [tab, setTab] = useState<SalesTab>('revenue');
  const [range, setRange] = useState<DateRangeValue>({ preset: '30d' });
  const period = range.preset;
  const [data, setData] = useState<SalesData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function compLabel(v: DateRangeValue): string {
    switch (v.preset) {
      case 'today':
        return t('sales.vsYesterday');
      case '7d':
        return t('sales.vsPrevious7');
      case '30d':
        return t('sales.vsPrevious30');
      case '90d':
        return t('sales.vsPrevious90');
      case 'this_month':
      case 'last_month':
        return t('sales.vsMonthBefore');
      case 'this_year':
        return t('sales.vsSameSpanLastYear');
      default:
        return t('sales.vsPreviousPeriod');
    }
  }

  useEffect(() => {
    if (!clientId) return;
    if (range.preset === 'custom' && (!range.from || !range.to)) return;
    setLoading(true);
    setError(null);
    const [start, end] = periodRange(range);
    fetchSales(clientId, start, end, range.preset === 'today', locale)
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : t('sales.loadFailed'));
        setLoading(false);
      });
  }, [clientId, range, locale, t]);

  const [rangeStart, rangeEnd] = periodRange(range);
  const fmtDate = (d: Date) =>
    d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });

  const onRange = setRange;
  const comp = compLabel(range);
  const aov = data && data.cur.orders > 0 ? data.cur.revenue / data.cur.orders : 0;
  const aovPrev = data && data.prev.orders > 0 ? data.prev.revenue / data.prev.orders : 0;
  const perCover = data && data.cur.pax > 0 ? data.cur.revenue / data.cur.pax : 0;
  const perCoverPrev = data && data.prev.pax > 0 ? data.prev.revenue / data.prev.pax : 0;
  const tipRate =
    data && data.tips.cardOrders > 0 ? (data.tips.tippedOrders / data.tips.cardOrders) * 100 : 0;
  const tipRatePrev =
    data && data.tips.prevCardOrders > 0
      ? (data.tips.prevTippedOrders / data.tips.prevCardOrders) * 100
      : 0;
  const avgTip = data && data.tips.tippedOrders > 0 ? data.tips.total / data.tips.tippedOrders : 0;
  const avgTipPrev =
    data && data.tips.prevTippedOrders > 0 ? data.tips.prevTotal / data.tips.prevTippedOrders : 0;

  return (
    <div className="sales-page">
      <PageHeader
        title={t('sales.title')}
        subtitle={
          !loading && data
            ? t('sales.subtitleRange', {
                from: fmtDate(rangeStart),
                to: fmtDate(rangeEnd),
                comparison: comp,
              })
            : undefined
        }
        actions={<DateRange value={range} onChange={onRange} />}
      />

      <Tabs
        value={tab}
        onChange={setTab}
        options={[
          { value: 'revenue', label: t('sales.tabRevenue') },
          { value: 'orders', label: t('sales.tabOrders') },
        ]}
      />

      {loading && (
        <div className="dash-loading">
          <div className="spinner" /> {t('common.loading')}
        </div>
      )}
      {error && (
        <Callout tone="danger" title={t('sales.couldntLoad')}>
          {error}
        </Callout>
      )}

      {!loading && data && tab === 'revenue' && (
        <div className="sales-content">
          <Stats>
            <Stat
              icon={<Wallet />}
              label={t('sales.revenue')}
              value={AMD(data.cur.revenue)}
              deltaPct={pctDelta(data.cur.revenue, data.prev.revenue)}
            />
            <Stat
              icon={<Receipt />}
              label={t('sales.paidOrders')}
              value={data.cur.orders}
              deltaPct={pctDelta(data.cur.orders, data.prev.orders)}
            />
            <Stat
              icon={<BarChart3 />}
              label={t('sales.averageOrder')}
              value={AMD(aov)}
              deltaPct={pctDelta(aov, aovPrev)}
            />
            <Stat
              icon={<Users />}
              label={t('sales.covers')}
              value={data.cur.pax}
              deltaPct={pctDelta(data.cur.pax, data.prev.pax)}
            />
            <Stat
              icon={<TrendingUp />}
              label={t('sales.revenuePerCover')}
              value={AMD(perCover)}
              deltaPct={pctDelta(perCover, perCoverPrev)}
            />
          </Stats>

          <Stats>
            <Stat
              icon={<CreditCard />}
              label={t('sales.cardTips')}
              value={AMD(data.tips.total)}
              deltaPct={pctDelta(data.tips.total, data.tips.prevTotal)}
            />
            <Stat
              icon={<Percent />}
              label={t('sales.tipRateOnCard')}
              value={fmtPct(tipRate)}
              deltaPct={pctDelta(tipRate, tipRatePrev)}
            />
            <Stat
              icon={<HandCoins />}
              label={t('sales.averageTipWhenTipped')}
              value={AMD(avgTip)}
              deltaPct={pctDelta(avgTip, avgTipPrev)}
            />
          </Stats>

          <p className="sales-note">{t('sales.note')}</p>

          <div className="dash-grid">
            <Card>
              <CardHeader
                title={t('sales.revenueTrend')}
                caption={period === 'today' ? t('sales.byHour') : t('sales.byDay')}
              />
              <TrendChart data={data.trend} isToday={period === 'today'} />
            </Card>
            <Card>
              <CardHeader title={t('sales.paymentSplit')} />
              <PaySplitChart data={data.paymentSplit} />
            </Card>
          </div>
        </div>
      )}

      {/* ── Orders tab ───────────────────────────────────────────── */}
      {!loading && data && tab === 'orders' && (
        <div className="sales-content">
          <div className="sales-stat-row">
            <StatBox label={t('sales.allOrders')} value={String(data.ordStats.total)} />
            <StatBox label={t('sales.paid')} value={String(data.ordStats.paid)} />
            <StatBox label={t('sales.refunded')} value={String(data.ordStats.refunded)} />
            <StatBox label={t('sales.void')} value={String(data.ordStats.voided)} />
            <StatBox label={t('sales.forceClosed')} value={String(data.ordStats.forced)} />
            <StatBox
              label={t('sales.refundRate')}
              value={
                data.ordStats.paid + data.ordStats.refunded > 0
                  ? fmtPct(
                      (data.ordStats.refunded / (data.ordStats.paid + data.ordStats.refunded)) *
                        100,
                    )
                  : t('common.emptyDash')
              }
            />
            <StatBox
              label={t('sales.forceCloseRate')}
              value={
                data.ordStats.total > 0
                  ? fmtPct((data.ordStats.forced / data.ordStats.total) * 100)
                  : t('common.emptyDash')
              }
            />
          </div>

          <div className="dash-grid">
            <Card>
              <CardHeader title={t('sales.forceCloseByReason')} />
              <ReasonChart data={data.reasons} />
            </Card>
            <Card>
              <CardHeader title={t('sales.orderQuality')} />
              <div className="quality-list">
                <div className="quality-row">
                  <span className="quality-label">{t('sales.avgItems')}</span>
                  <span className="quality-value">
                    {data.avgItems !== null ? data.avgItems.toFixed(1) : t('common.emptyDash')}
                  </span>
                </div>
                <div className="quality-row">
                  <span className="quality-label">{t('sales.modifierAttach')}</span>
                  <span className="quality-value">
                    {data.modRate !== null ? fmtPct(data.modRate) : t('common.emptyDash')}
                  </span>
                </div>
                <div className="quality-row">
                  <span className="quality-label">{t('sales.paidShare')}</span>
                  <span className="quality-value">
                    {data.ordStats.total > 0
                      ? fmtPct((data.ordStats.paid / data.ordStats.total) * 100)
                      : t('common.emptyDash')}
                  </span>
                </div>
                <div className="quality-row">
                  <span className="quality-label">{t('sales.totalCovers')}</span>
                  <span className="quality-value">{data.cur.pax}</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
